import { ActionIcon, Badge, Group, Menu, Text, Tooltip } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCityPipeline } from '../hooks/use-city-pipeline';
import { MenuHelpIcon } from './help-hint';

type Props = {
    /** Preferred city id from list filter; if missing, prompts. */
    cityId?: number | null;
    onFinished?: () => void;
};

export const CityPipelineBar = ({ cityId, onFinished }: Props) => {
    const { t } = useTranslation();
    const {
        pipeline,
        running,
        autolinkLoading,
        gapAuditLoading,
        startPipeline,
        stopPipeline,
        autolinkCity,
        auditGaps,
    } = useCityPipeline();

    const resolveCityId = (): number | null => {
        if (cityId) return cityId;

        const raw = window.prompt(t('pipeline.promptCityId'));

        if (!raw) return null;
        const id = Number(raw);

        return Number.isFinite(id) && id > 0 ? id : null;
    };

    return (
        <Group gap='xs' wrap='nowrap' style={{ flexShrink: 0 }}>
            <Menu shadow='md' width={280} position='bottom-end'>
                <Menu.Target>
                    <ActionIcon
                        variant='default'
                        size='lg'
                        aria-label={t('pipeline.cityTools')}
                        title={t('pipeline.cityTools')}
                    >
                        <IconDots size={18} />
                    </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                    <Tooltip
                        label={t('pipeline.help.processCity')}
                        multiline
                        maw={340}
                        withArrow
                        position='left'
                        events={{ hover: true, focus: true, touch: true }}
                    >
                        <Menu.Item
                            rightSection={<MenuHelpIcon />}
                            disabled={running}
                            onClick={async () => {
                                const id = resolveCityId();

                                if (!id) return;
                                await startPipeline(id);
                                onFinished?.();
                            }}
                        >
                            {running
                                ? `${t('pipeline.processingStatus', {
                                      city: pipeline?.city,
                                      stage: pipeline?.stage || t('pipeline.starting'),
                                  })}${pipeline?.stageDetail ? ` — ${pipeline.stageDetail}` : ''}`
                                : t('pipeline.processCity')}
                        </Menu.Item>
                    </Tooltip>
                    <Tooltip
                        label={t('pipeline.help.autolink')}
                        multiline
                        maw={340}
                        withArrow
                        position='left'
                        events={{ hover: true, focus: true, touch: true }}
                    >
                        <Menu.Item
                            rightSection={<MenuHelpIcon />}
                            disabled={running || autolinkLoading}
                            onClick={async () => {
                                const id = resolveCityId();

                                if (!id) return;
                                await autolinkCity(id);
                                onFinished?.();
                            }}
                        >
                            {t('pipeline.autolink')}
                        </Menu.Item>
                    </Tooltip>
                    <Tooltip
                        label={t('pipeline.help.gapAudit')}
                        multiline
                        maw={340}
                        withArrow
                        position='left'
                        events={{ hover: true, focus: true, touch: true }}
                    >
                        <Menu.Item
                            rightSection={<MenuHelpIcon />}
                            disabled={running || gapAuditLoading}
                            onClick={async () => {
                                const id = resolveCityId();

                                if (!id) return;
                                await auditGaps(id);
                                onFinished?.();
                            }}
                        >
                            {gapAuditLoading
                                ? t('pipeline.gapAuditRunning')
                                : t('pipeline.gapAudit')}
                        </Menu.Item>
                    </Tooltip>
                    {running && (
                        <Tooltip
                            label={t('pipeline.help.stop')}
                            multiline
                            maw={340}
                            withArrow
                            position='left'
                            events={{ hover: true, focus: true, touch: true }}
                        >
                            <Menu.Item
                                color='red'
                                rightSection={<MenuHelpIcon />}
                                onClick={() => void stopPipeline()}
                            >
                                {t('pipeline.stop')}
                            </Menu.Item>
                        </Tooltip>
                    )}
                </Menu.Dropdown>
            </Menu>
            {pipeline && !running && (
                <Badge color={pipeline.status === 'done' ? 'green' : 'red'} variant='light'>
                    {t('pipeline.lastRun', { id: pipeline.id, status: pipeline.status })}
                </Badge>
            )}
            {running && (
                <Text size='xs' c='dimmed' style={{ whiteSpace: 'nowrap' }}>
                    {t('pipeline.running')}
                </Text>
            )}
        </Group>
    );
};
